import { Framework } from '@/framework';
import type {
    DeepReadonly,
    StageHistoryExtensionApplyEvent,
    StageHistoryExtensionCollector,
    StageHistoryExtensionContext,
    StageHistoryExtensionListener,
    StageHistoryExtensions,
    StageHistorySerializableValue,
    StageOperate,
    StageOperateEventListener,
    StageResizePayload,
} from './operation-events';

/**
 * StageOperate 实例内部使用的同步事件名称。
 * 跨插件只能通过实例的 on* 方法订阅，不能直接依赖这些字符串。
 */
const STAGE_OPERATE_EVENT_NAMES = {
    RESIZE_START: 'stage-operate:resize:start',
    RESIZING: 'stage-operate:resize:resizing',
    RESIZE_END: 'stage-operate:resize:end',
    COLLECT_HISTORY_EXTENSIONS: 'stage-operate:history:collect-extensions',
    APPLY_HISTORY_EXTENSIONS: 'stage-operate:history:apply-extensions',
} as const;

/** 判断并复制 JSON 安全值，避免历史持有监听器仍可修改的引用。 */
const cloneSerializableValue = (
    value: StageHistorySerializableValue,
    ancestors = new Set<object>(),
): StageHistorySerializableValue => {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) throw new TypeError('舞台历史扩展只允许有限数值');
        return value;
    }
    if (typeof value !== 'object') throw new TypeError('舞台历史扩展包含不可序列化值');
    if (ancestors.has(value)) throw new TypeError('舞台历史扩展不能包含循环引用');

    ancestors.add(value);
    if (Array.isArray(value)) {
        const clonedValue = value.map((item) => cloneSerializableValue(item, ancestors));
        ancestors.delete(value);
        return clonedValue;
    }

    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        throw new TypeError('舞台历史扩展只允许普通对象');
    }
    if (Object.getOwnPropertySymbols(value).length > 0) {
        throw new TypeError('舞台历史扩展不能包含 symbol 属性');
    }

    const clonedValue: Record<string, StageHistorySerializableValue> = {};
    Object.entries(value).forEach(([key, item]) => {
        // defineProperty 可把 __proto__ 保留为普通 JSON 键，避免赋值触发 Object.prototype setter。
        Object.defineProperty(clonedValue, key, {
            configurable: true,
            enumerable: true,
            value: cloneSerializableValue(item, ancestors),
            writable: true,
        });
    });
    ancestors.delete(value);
    return clonedValue;
};

/** 为监听器创建与舞台历史核心入参隔离的深只读上下文。 */
const cloneHistoryContext = (context: StageHistoryExtensionContext): DeepReadonly<StageHistoryExtensionContext> => {
    if (context.operation === 'resize') return { ...context };
    return {
        ...context,
        oldSize: { ...context.oldSize },
        newSize: { ...context.newSize },
    };
};

/**
 * stage-operate 内部事件实现。
 * 上层插件只从插件入口获得 StageOperate 订阅接口，不能调用本类的 emit* 方法。
 */
export class StageOperateImpl extends Framework implements StageOperate {
    /** 监听舞台尺寸调整开始；返回函数用于解绑。 */
    onResizeStart(listener: StageOperateEventListener<void>) {
        return this.subscribe(STAGE_OPERATE_EVENT_NAMES.RESIZE_START, listener);
    }

    /** 监听舞台尺寸调整过程；返回函数用于解绑。 */
    onResizing(listener: StageOperateEventListener<StageResizePayload>) {
        return this.subscribe(STAGE_OPERATE_EVENT_NAMES.RESIZING, listener);
    }

    /** 监听舞台尺寸调整结束；返回函数用于解绑。 */
    onResizeEnd(listener: StageOperateEventListener<void>) {
        return this.subscribe(STAGE_OPERATE_EVENT_NAMES.RESIZE_END, listener);
    }

    /** 监听舞台历史扩展数据采集；返回函数用于解绑。 */
    onCollectHistoryExtensions(listener: StageHistoryExtensionListener) {
        return this.subscribe(STAGE_OPERATE_EVENT_NAMES.COLLECT_HISTORY_EXTENSIONS, listener);
    }

    /** 监听舞台历史扩展数据应用；返回函数用于解绑。 */
    onApplyHistoryExtensions(listener: StageOperateEventListener<StageHistoryExtensionApplyEvent>) {
        return this.subscribe(STAGE_OPERATE_EVENT_NAMES.APPLY_HISTORY_EXTENSIONS, listener);
    }

    /** 通知上层插件舞台尺寸调整开始。 */
    emitResizeStart() {
        this.emit(STAGE_OPERATE_EVENT_NAMES.RESIZE_START, undefined);
    }

    /** 通知上层插件舞台尺寸正在调整。 */
    emitResizing(payload: StageResizePayload) {
        this.emit(STAGE_OPERATE_EVENT_NAMES.RESIZING, payload);
    }

    /** 通知上层插件舞台尺寸调整结束。 */
    emitResizeEnd() {
        this.emit(STAGE_OPERATE_EVENT_NAMES.RESIZE_END, undefined);
    }

    /**
     * 同步采集上层插件的舞台历史扩展数据。
     * 监听器接收隔离上下文，返回对象和值均由本次历史写入独占。
     */
    collectHistoryExtensions(context: StageHistoryExtensionContext): StageHistoryExtensions {
        const extensions: StageHistoryExtensions = {};
        const collector: StageHistoryExtensionCollector = {
            set(namespace, value) {
                if (!namespace.trim()) throw new TypeError('舞台历史扩展命名空间不能为空');
                if (Object.prototype.hasOwnProperty.call(extensions, namespace)) {
                    throw new Error(`舞台历史扩展命名空间重复：${namespace}`);
                }
                extensions[namespace] = cloneSerializableValue(value);
            },
        };

        this.emit(STAGE_OPERATE_EVENT_NAMES.COLLECT_HISTORY_EXTENSIONS, cloneHistoryContext(context), collector);
        return extensions;
    }

    /** 在舞台核心历史恢复到原有位置后通知上层插件应用扩展数据。 */
    emitApplyHistoryExtensions(event: StageHistoryExtensionApplyEvent) {
        this.emit(STAGE_OPERATE_EVENT_NAMES.APPLY_HISTORY_EXTENSIONS, event);
    }

    /** 封装无类型事件总线，并避免 listener 返回 true 中断后续监听器。 */
    private subscribe<TArgs extends unknown[]>(eventName: string, listener: (...args: TArgs) => void) {
        const handler = this.framework.service.events.on(eventName, (...args: TArgs) => {
            listener(...args);
        });
        return () => this.framework.service.events.off(handler);
    }

    /** 同步发送实例内部事件；监听器错误保持向上传播。 */
    private emit<TArgs extends unknown[]>(eventName: string, ...args: TArgs) {
        this.framework.service.events.emit(eventName, ...args);
    }
}
